/**
 * A tiny arithmetic evaluator over named terms — "A - B", "(A - B) / B * 100".
 *
 * Deliberately NOT `eval`: the expression is author-typed and gets run once
 * per cell, so it is compiled to a closure over a scope object and nothing
 * else is reachable from it. Grammar, lowest precedence first:
 *
 *   expr   := term (("+" | "-") term)*
 *   term   := factor (("*" | "/") factor)*
 *   factor := number | TERM_KEY | "(" expr ")" | "-" factor
 *
 * compileExpression returns { evaluate, baseline, error }. `evaluate(scope)`
 * answers a number, or null when a divisor is zero — a cell with no
 * denominator has no value rather than an Infinity.
 *
 * `baseline(scope)` is what the expression SUBTRACTS at its outermost level —
 * B - A → A, A - (B + C) → B + C — the denominator of a percent change. Null
 * when the outermost operation is not a subtraction (A + B, (A - B) * 2, a
 * whole expression wrapped in brackets): no single thing is being compared
 * against, so there is no percent change to take.
 */
const TOKEN = /\s*([A-Z]|\d+(?:\.\d+)?|[()+\-*/])/y;

const tokenize = (rawInput) => {
  const input = rawInput.trim();
  const tokens = [];
  let at = 0;
  while (at < input.length) {
    TOKEN.lastIndex = at;
    const match = TOKEN.exec(input);
    if (!match) throw new Error(`Unexpected character "${input[at].trim() || input[at]}"`);
    tokens.push(match[1]);
    at = TOKEN.lastIndex;
  }
  return tokens;
};

export const compileExpression = (input, termKeys) => {
  if (!input || !input.trim()) return { evaluate: null, baseline: null, error: "Write an expression, e.g. A - B" };
  let tokens;
  try {
    tokens = tokenize(input);
  } catch (err) {
    return { evaluate: null, baseline: null, error: err.message };
  }

  // Brackets around the WHOLE expression change nothing about its value, but
  // they would hide its outermost subtraction from `baseline` — "(B - A)"
  // compares the same way "B - A" does. Peel them off first.
  const wrapsWhole = (list) => {
    if (list[0] !== "(" || list[list.length - 1] !== ")") return false;
    let depth = 0;
    for (let i = 0; i < list.length; i += 1) {
      if (list[i] === "(") depth += 1;
      if (list[i] === ")") depth -= 1;
      if (depth === 0 && i < list.length - 1) return false;
    }
    return true;
  };
  while (tokens.length > 2 && wrapsWhole(tokens)) tokens = tokens.slice(1, -1);

  let at = 0;
  const peek = () => tokens[at];
  const eat = (token) => {
    if (peek() !== token) throw new Error(`Expected "${token}"`);
    at += 1;
  };

  const parseFactor = () => {
    const token = peek();
    if (token === undefined) throw new Error("The expression ends too early");
    if (token === "(") {
      at += 1;
      const inner = parseExpr();
      eat(")");
      return inner;
    }
    if (token === "-") {
      at += 1;
      const inner = parseFactor();
      return (scope) => -inner(scope);
    }
    at += 1;
    if (/^[A-Z]$/.test(token)) {
      if (!termKeys.includes(token)) throw new Error(`There is no term ${token}`);
      return (scope) => scope[token] || 0;
    }
    if (/^\d/.test(token)) {
      const value = Number(token);
      return () => value;
    }
    throw new Error(`Unexpected "${token}"`);
  };

  const parseTerm = () => {
    let left = parseFactor();
    while (peek() === "*" || peek() === "/") {
      const op = peek();
      at += 1;
      const right = parseFactor();
      const l = left;
      left =
        op === "*"
          ? (scope) => l(scope) * right(scope)
          : (scope) => {
              const divisor = right(scope);
              const value = divisor === 0 ? null : l(scope) / divisor;
              return value;
            };
    }
    return left;
  };

  // `track`, passed only by the outermost call, records the last operation of
  // the top-level chain — A + B - C is (A + B) - C, so it ends in "-" with C.
  function parseExpr(track) {
    let left = parseTerm();
    let top = null;
    while (peek() === "+" || peek() === "-") {
      const op = peek();
      at += 1;
      const right = parseTerm();
      const l = left;
      left = op === "+" ? (scope) => l(scope) + right(scope) : (scope) => l(scope) - right(scope);
      top = { op, right };
    }
    if (track) track.top = top;
    return left;
  }

  let compiled;
  const track = {};
  try {
    compiled = parseExpr(track);
    if (at < tokens.length) throw new Error(`Unexpected "${tokens[at]}"`);
  } catch (err) {
    return { evaluate: null, baseline: null, error: err.message };
  }

  const finite = (value) => (value === null || Number.isNaN(value) || !Number.isFinite(value) ? null : value);
  const subtracted = track.top?.op === "-" ? track.top.right : null;
  return {
    evaluate: (scope) => finite(compiled(scope)),
    baseline: subtracted ? (scope) => finite(subtracted(scope)) : null,
    error: null,
  };
};
